import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI } from '../../services/api';
import { 
  HiOutlineBell, 
  HiOutlineClock, 
  HiOutlineChevronDown, 
  HiOutlineChevronUp, 
  HiOutlineCheckCircle, 
  HiOutlineOfficeBuilding, 
  HiOutlineUser, 
  HiOutlineTag,
  HiOutlineInformationCircle
} from 'react-icons/hi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { TabBar, TabBtn } from '../../components/common/Tabs';
import { useOrg } from '../../context/OrgContext';

// Metadata keys whose linked record is shown as its own table
const RESOLVED_ID_KEYS = ['salesOrderId', 'dispatchId', 'gateEntryId'];

// Admin trace filters; module names match backend/middleware/activityTrace.js
const VIEW_TABS = [
  { key: 'activity', label: 'Activity Trace' },
  { key: 'alerts', label: 'Alerts' },
  { key: '', label: 'All Events' }
];
const MODULES = [
  'Gate', 'Sales', 'Orders', 'Production', 'Quality', 'Store', 'Dispatch',
  'Purchase & Finance', 'HR', 'User Management', 'Organization', 'Account', 'Support', 'Domain Builder'
];
const ROLES = [
  'admin', 'super_admin', 'gate_guard', 'sales', 'supervisor', 'production_manager', 'quality_checker',
  'store_manager', 'dispatch', 'dispatch_manager', 'purchase_manager', 'purchase_user',
  'assembly', 'assembly_user', 'assembly_manager', 'user'
];
const EMPTY_FILTERS = { category: 'activity', module: '', role: '', search: '', from: '', to: '' };
const LIVE_REFRESH_MS = 20000;

const humanize = (key) => String(key)
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\b\w/g, c => c.toUpperCase());

const DetailTable = ({ title, rows }) => (
  <div style={{ marginTop: '16px' }}>
    <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
      {title}
    </div>
    <div className="azure-table-container" style={{ marginBottom: 0, overflowX: 'auto' }}>
      <table className="azure-table">
        <tbody>
          {rows.map(row => (
            <tr key={row.label}>
              <td style={{ width: '32%', fontWeight: 600, color: 'var(--text-muted)', background: 'rgba(248, 250, 252, 0.65)', padding: '10px 16px' }}>
                {row.label}
              </td>
              <td style={{ color: 'var(--text-primary)', padding: '10px 16px', wordBreak: 'break-word' }}>
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const AdminNotificationDetails = ({ entry, isSuperAdmin, formatCurrency }) => {
  if (!entry || entry.loading) {
    return <div style={{ padding: '16px 0', color: 'var(--text-muted)', fontSize: '0.85rem' }}>Loading full details...</div>;
  }
  if (entry.error) {
    return <div style={{ padding: '16px 0', color: 'var(--danger, #dc2626)', fontSize: '0.85rem' }}>{entry.error}</div>;
  }

  const d = entry.data;
  const formatValue = (value, format) => {
    if (format === 'date') return new Date(value).toLocaleString();
    if (format === 'currency') return formatCurrency(value);
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const overviewRows = [
    { label: 'Title', value: d.title },
    { label: 'Message', value: d.message },
    { label: 'Event Type', value: humanize(d.type || 'system') },
    { label: 'Date & Time', value: new Date(d.createdAt).toLocaleString() },
    isSuperAdmin && d.organization && { label: 'Organization', value: d.organization.name },
    isSuperAdmin && d.organization?.contactEmail && { label: 'Organization Email', value: d.organization.contactEmail },
    isSuperAdmin && d.organization?.contactPhone && { label: 'Organization Phone', value: d.organization.contactPhone },
    { label: 'Triggered By', value: d.actionBy ? `${d.actionBy.name} (${humanize(d.actionBy.role || '')})` : 'System' },
    d.actionBy?.email && { label: 'Triggered By Email', value: d.actionBy.email },
    d.actionBy?.phone && { label: 'Triggered By Phone', value: d.actionBy.phone },
    d.actionBy?.department && { label: 'Department', value: d.actionBy.department },
    { label: 'Sent To', value: d.targetRoles?.length ? d.targetRoles.map(humanize).join(', ') : 'Everyone in organization' },
    { label: 'Seen By', value: d.readBy?.length ? d.readBy.map(u => `${u.name} (${humanize(u.role || '')})`).join(', ') : 'Nobody yet' }
  ].filter(Boolean);

  const metadataRows = Object.entries(d.metadata || {})
    .filter(([key, value]) => !(d.related && RESOLVED_ID_KEYS.includes(key)) && value !== null && value !== undefined && value !== '')
    .map(([key, value]) => ({
      label: humanize(key),
      value: formatValue(value, typeof value === 'number' && /total|amount/i.test(key) ? 'currency' : undefined)
    }));

  const relatedRows = (d.related?.rows || []).map(r => ({
    label: r.label,
    value: formatValue(r.value, r.format)
  }));

  return (
    <>
      <DetailTable title="Notification Details" rows={overviewRows} />
      {metadataRows.length > 0 && <DetailTable title="Event Data (at time of notification)" rows={metadataRows} />}
      {relatedRows.length > 0 && <DetailTable title={`${d.related.kind} Details`} rows={relatedRows} />}
    </>
  );
};

const NotificationsPage = () => {
  const { user } = useAuth();
  const { formatCurrency } = useOrg();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [expandedId, setExpandedId] = useState(null);
  // Full details (fetched on demand) for org admins and the platform admin
  const [details, setDetails] = useState({});
  const isAdminViewer = ['admin', 'super_admin'].includes(user?.role);
  const isPlatformAdmin = user?.role === 'super_admin' && user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL;
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Search box is applied after the admin stops typing
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setFilters(prev => (prev.search === searchInput ? prev : { ...prev, search: searchInput })), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters(prev => ({ ...EMPTY_FILTERS, category: prev.category }));
    setSearchInput('');
  };
  const hasFilters = ['module', 'role', 'search', 'from', 'to'].some(k => filters[k]);

  const fetchDetails = async (id) => {
    setDetails(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const res = await notificationAPI.getNotificationDetails(id);
      setDetails(prev => ({ ...prev, [id]: { data: res.data.data } }));
    } catch (err) {
      console.error('Failed to fetch notification details', err);
      setDetails(prev => ({ ...prev, [id]: { error: err.response?.data?.message || 'Could not load full details.' } }));
    }
  };

  const fetchNotifications = async (page = 1, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const activeFilters = isAdminViewer
        ? Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
        : {};
      const res = isPlatformAdmin
        ? await notificationAPI.getSuperadminNotifications(page, 10, activeFilters)
        : await notificationAPI.getNotifications(page, 10, activeFilters);

      setNotifications(res.data.data);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      }
    } catch (err) {
      console.error('Failed to fetch notifications', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);
    setExpandedId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, filters]);

  // Live trace: keep the first page fresh while the admin watches it
  useEffect(() => {
    if (!isAdminViewer || pagination.page !== 1) return undefined;
    const interval = setInterval(() => fetchNotifications(1, { silent: true }), LIVE_REFRESH_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminViewer, pagination.page, filters]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      fetchNotifications(newPage);
      setExpandedId(null);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await notificationAPI.markAsRead(id);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
    } catch (err) {
      console.error(err);
    }
  };

  const toggleExpand = (notification) => {
    const isExpanding = expandedId !== notification._id;
    setExpandedId(isExpanding ? notification._id : null);
    if (isExpanding && isAdminViewer && !details[notification._id]?.data) {
      fetchDetails(notification._id);
    }
    if (isExpanding && !notification.isRead) {
      handleMarkRead(notification._id);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <DashboardLayout pageTitle="Notifications">
      <div className="card" style={{ maxWidth: '1000px', margin: '0 auto', minHeight: '80vh', padding: '24px' }}>
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '10px', fontSize: '1.4rem', fontWeight: 800 }}>
              <span style={{ 
                width: '36px', height: '36px', borderRadius: '8px', 
                background: 'rgba(var(--primary-rgb), 0.12)', color: 'var(--primary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center' 
              }}>
                <HiOutlineBell />
              </span>
              Notifications Center
            </h2>
            <p style={{ margin: '4px 0 0 0', fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Real-time activity logs and system alerts
            </p>
          </div>

          <button 
            className="btn btn-secondary" 
            style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.85rem' }} 
            onClick={handleMarkAllRead}
          >
            <HiOutlineCheckCircle /> Mark All Read
          </button>
        </div>

        {/* Admin: event trace across all users & departments */}
        {isAdminViewer && (
          <div style={{ marginBottom: '20px' }}>
            <div className="tab-row" style={{ marginBottom: '12px' }}>
              <TabBar>
                {VIEW_TABS.map(tab => (
                  <TabBtn key={tab.key || 'all'} active={filters.category === tab.key} onClick={() => updateFilter('category', tab.key)}>
                    {tab.label}
                  </TabBtn>
                ))}
              </TabBar>
              <span className="tab-row-actions" style={{ fontSize: '0.75rem', color: 'var(--text-dim)', paddingTop: '16px' }}>
                {pagination.total} events · auto-refreshes every 20s
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', alignItems: 'end' }}>
              <input
                type="text"
                className="form-input"
                placeholder="Search user, record, action..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                style={{ padding: '10px 12px', fontSize: '0.85rem' }}
              />
              <select
                className="form-input"
                value={filters.module}
                onChange={e => updateFilter('module', e.target.value)}
                style={{ padding: '10px 12px', fontSize: '0.85rem' }}
              >
                <option value="">All departments</option>
                {MODULES.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
              <select
                className="form-input"
                value={filters.role}
                onChange={e => updateFilter('role', e.target.value)}
                style={{ padding: '10px 12px', fontSize: '0.85rem' }}
              >
                <option value="">All roles</option>
                {ROLES.map(r => <option key={r} value={r}>{humanize(r)}</option>)}
              </select>
              <input
                type="date"
                className="form-input"
                title="From date"
                value={filters.from}
                onChange={e => updateFilter('from', e.target.value)}
                style={{ padding: '9px 12px', fontSize: '0.85rem' }}
              />
              <input
                type="date"
                className="form-input"
                title="To date"
                value={filters.to}
                onChange={e => updateFilter('to', e.target.value)}
                style={{ padding: '9px 12px', fontSize: '0.85rem' }}
              />
              {hasFilters && (
                <button className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '9px 12px' }} onClick={clearFilters}>
                  Clear filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* List Content */}
        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading notifications...
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '60px', textAlign: 'center', color: 'var(--text-dim)', background: 'var(--bg-input)', borderRadius: '12px' }}>
            <HiOutlineInformationCircle style={{ fontSize: '2.5rem', marginBottom: '8px', opacity: 0.6 }} />
            <h4>No notifications yet</h4>
            <p style={{ fontSize: '0.85rem', margin: 0 }}>You're all caught up with the latest alerts.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {notifications.map(n => {
              const isExpanded = expandedId === n._id;
              return (
                <div 
                  key={n._id} 
                  style={{ 
                    borderRadius: '12px',
                    border: !n.isRead ? '1px solid var(--primary)' : '1px solid var(--border)',
                    background: !n.isRead ? 'rgba(var(--primary-rgb), 0.04)' : 'var(--bg-card)',
                    boxShadow: isExpanded ? '0 8px 24px rgba(0,0,0,0.12)' : '0 2px 6px rgba(0,0,0,0.04)',
                    transition: 'all 0.25s ease',
                    overflow: 'hidden'
                  }}
                >
                  {/* Summary Bar (Clickable) */}
                  <div 
                    onClick={() => toggleExpand(n)}
                    style={{ 
                      padding: '16px 20px', 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      userSelect: 'none'
                    }}
                  >
                    <div style={{ 
                      width: '42px', height: '42px', borderRadius: '10px', 
                      background: !n.isRead ? 'var(--primary)' : 'var(--bg-input)', 
                      color: !n.isRead ? '#ffffff' : 'var(--primary)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', 
                      fontSize: '1.3rem', flexShrink: 0 
                    }}>
                      <HiOutlineBell />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '4px' }}>
                        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: !n.isRead ? 700 : 600, color: 'var(--text-primary)' }}>
                          {n.title}
                        </h4>
                        
                        {!n.isRead && (
                          <span style={{ 
                            fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', 
                            background: 'var(--primary)', color: '#fff', padding: '2px 7px', borderRadius: '6px' 
                          }}>
                            New
                          </span>
                        )}

                        {isAdminViewer && n.metadata?.module && (
                          <span style={{
                            fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: '8px',
                            background: n.type === 'activity' ? 'rgba(100, 116, 139, 0.12)' : 'rgba(var(--primary-rgb), 0.12)',
                            color: n.type === 'activity' ? 'var(--text-secondary)' : 'var(--primary)'
                          }}>
                            {n.metadata.module}{n.type === 'activity' ? '' : ' · Alert'}
                          </span>
                        )}

                        {user?.role === 'super_admin' && n.organization && (
                          <span style={{
                            background: `${n.organization.color || '#3b82f6'}20`,
                            color: n.organization.color || '#3b82f6', 
                            padding: '2px 8px', borderRadius: '8px', fontWeight: 700, fontSize: '0.72rem',
                            display: 'inline-flex', alignItems: 'center', gap: '4px'
                          }}>
                            <HiOutlineOfficeBuilding /> {n.organization.name}
                          </span>
                        )}
                      </div>

                      <p style={{ 
                        margin: 0, 
                        fontSize: '0.85rem', 
                        color: 'var(--text-secondary)',
                        whiteSpace: isExpanded ? 'normal' : 'nowrap',
                        overflow: isExpanded ? 'visible' : 'hidden',
                        textOverflow: isExpanded ? 'clip' : 'ellipsis'
                      }}>
                        {n.message}
                      </p>
                    </div>

                    {/* Right side timestamp & Expand Arrow */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <HiOutlineClock /> {new Date(n.createdAt).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </span>
                      
                      <button 
                        style={{
                          background: 'none', border: 'none', color: 'var(--primary)',
                          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px',
                          fontSize: '0.82rem', fontWeight: 600, padding: '4px 8px'
                        }}
                      >
                        {isExpanded ? (
                          <><span>Less</span> <HiOutlineChevronUp /></>
                        ) : (
                          <><span>View More</span> <HiOutlineChevronDown /></>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Inline Expanded Details (Accordion In-Place) */}
                  {isExpanded && isAdminViewer && (
                    <div style={{
                      padding: '0 20px 20px 20px',
                      borderTop: '1px solid var(--border-light)',
                      marginTop: '4px',
                      background: 'rgba(0,0,0,0.02)'
                    }}>
                      <AdminNotificationDetails
                        entry={details[n._id]}
                        isSuperAdmin={user?.role === 'super_admin'}
                        formatCurrency={formatCurrency}
                      />
                    </div>
                  )}

                  {isExpanded && !isAdminViewer && (
                    <div style={{
                      padding: '0 20px 20px 20px', 
                      borderTop: '1px solid var(--border-light)',
                      marginTop: '4px',
                      background: 'rgba(0,0,0,0.02)'
                    }}>
                      <div style={{ 
                        background: 'var(--bg-input)', 
                        padding: '16px', 
                        borderRadius: '10px', 
                        margin: '16px 0 12px 0',
                        fontSize: '0.92rem', 
                        lineHeight: 1.6, 
                        color: 'var(--text-primary)' 
                      }}>
                        {n.message}
                      </div>

                      {/* Meta Information Badges */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                        gap: '12px', 
                        fontSize: '0.8rem', 
                        color: 'var(--text-secondary)' 
                      }}>
                        <div style={{ background: 'var(--bg-card)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                          <span style={{ color: 'var(--text-dim)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '2px' }}>Timestamp</span>
                          <strong>{new Date(n.createdAt).toLocaleString()}</strong>
                        </div>

                        <div style={{ background: 'var(--bg-card)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                          <span style={{ color: 'var(--text-dim)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '2px' }}>Event Type</span>
                          <strong style={{ textTransform: 'capitalize' }}>{n.type?.replace(/_/g, ' ') || 'General Alert'}</strong>
                        </div>

                        {n.actionBy && (
                          <div style={{ background: 'var(--bg-card)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                            <span style={{ color: 'var(--text-dim)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '2px' }}>Triggered By</span>
                            <strong>{n.actionBy.name}</strong> <span style={{ color: 'var(--text-muted)' }}>({n.actionBy.role})</span>
                          </div>
                        )}

                        {user?.role === 'super_admin' && n.organization && (
                          <div style={{ background: 'var(--bg-card)', padding: '10px 14px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                            <span style={{ color: 'var(--text-dim)', display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', marginBottom: '2px' }}>Organization</span>
                            <strong style={{ color: n.organization.color || 'var(--primary)' }}>{n.organization.name}</strong>
                          </div>
                        )}
                      </div>

                      {/* Attached Metadata if available */}
                      {n.metadata && Object.keys(n.metadata).length > 0 && (
                        <div style={{ marginTop: '14px' }}>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-muted)', display: 'block', marginBottom: '6px' }}>Attached Metadata Details:</span>
                          <pre style={{ 
                            background: '#181e29', color: '#38bdf8', 
                            padding: '12px 16px', borderRadius: '8px', 
                            fontSize: '0.78rem', margin: 0, overflowX: 'auto' 
                          }}>
                            {JSON.stringify(n.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Pagination Controls (10 per page) */}
            {pagination.pages > 1 && (
              <div style={{ 
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                marginTop: '20px', padding: '14px 18px', 
                background: 'var(--bg-card)', borderRadius: '10px', border: '1px solid var(--border)' 
              }}>
                <button 
                  className="btn btn-secondary" 
                  disabled={pagination.page === 1}
                  onClick={() => handlePageChange(pagination.page - 1)}
                  style={{ fontSize: '0.85rem' }}
                >
                  ← Previous
                </button>
                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                </span>
                <button 
                  className="btn btn-secondary" 
                  disabled={pagination.page === pagination.pages}
                  onClick={() => handlePageChange(pagination.page + 1)}
                  style={{ fontSize: '0.85rem' }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default NotificationsPage;
